import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { MessageCircle, UserPlus, UserMinus, Ban, Calendar, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  online: 'bg-green-500',
  idle: 'bg-yellow-500',
  dnd: 'bg-red-500',
  offline: 'bg-zinc-600',
};

export const UserProfilePopup = ({ userId, open, onClose }) => {
  const { user: currentUser, token } = useAuth();
  const { friends, sendFriendRequest, removeFriend, createDM, selectDm, fetchDMs } = useApp();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId || !open) return;
    setLoading(true);
    fetch(`${API_URL}/api/users/${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { setProfile(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId, open, token]);

  if (!open) return null;

  const isSelf = userId === currentUser?.user_id;
  const isFriend = friends.some(f => f.user_id === userId);
  const displayName = profile?.display_name || profile?.username || '';
  const bannerColor = profile?.banner || 'linear-gradient(135deg, #0891b2 0%, #164e63 100%)';

  const handleSendDM = async () => {
    const dm = await createDM(userId);
    if (dm) {
      await fetchDMs();
      selectDm({ ...dm, user: profile });
      onClose();
    }
  };

  const handleAddFriend = async () => {
    try { await sendFriendRequest(userId); } catch (e) { console.error(e); }
  };

  const handleRemoveFriend = async () => {
    await removeFriend(userId);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#12141a] border-white/5 text-white p-0 max-w-[340px] rounded-xl overflow-hidden" data-testid="user-profile-popup">
        <DialogTitle className="sr-only">User Profile</DialogTitle>
        {loading || !profile ? (
          <div className="h-60 flex items-center justify-center">
            <div className="h-6 w-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Banner */}
            <div
              className="h-[100px] relative"
              style={{
                background: profile.banner
                  ? `url(${profile.banner}) center/cover`
                  : 'linear-gradient(135deg, #0891b2 0%, #164e63 100%)'
              }}
              data-testid="profile-banner"
            />

            {/* Avatar */}
            <div className="px-4 -mt-10 relative z-10">
              <div className="relative inline-block">
                <Avatar className="h-[80px] w-[80px] border-[5px] border-[#12141a]">
                  <AvatarImage src={profile.avatar} />
                  <AvatarFallback className="bg-cyan-600/30 text-cyan-200 text-2xl">{displayName[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className={`absolute bottom-1 right-1 h-5 w-5 rounded-full border-[3px] border-[#12141a] ${statusColors[profile.status] || statusColors.offline}`} />
              </div>
            </div>

            {/* Info */}
            <div className="px-4 pt-2 pb-4">
              <h3 className="text-lg font-bold text-white" data-testid="profile-display-name">{displayName}</h3>
              <p className="text-sm text-zinc-400" data-testid="profile-username">@{profile.username}</p>

              {/* Badges */}
              {profile.badges_info && profile.badges_info.length > 0 && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {profile.badges_info.map(b => (
                    <span
                      key={b.badge_id}
                      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border"
                      style={{ borderColor: b.color + '40', color: b.color, backgroundColor: b.color + '15' }}
                      title={b.description}
                      data-testid={`badge-${b.badge_id}`}
                    >
                      {b.icon} {b.name}
                    </span>
                  ))}
                </div>
              )}

              <Separator className="bg-white/5 my-3" />

              {/* Bio */}
              {profile.bio && (
                <div className="mb-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">{t('app.bio')}</p>
                  <p className="text-sm text-zinc-300 leading-relaxed" data-testid="profile-bio">{profile.bio}</p>
                </div>
              )}

              {/* Roles */}
              {profile.roles && profile.roles.length > 0 && (
                <div className="mb-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">{t('app.roles')}</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {profile.roles.map(r => (
                      <span
                        key={r.role_id}
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: r.color + '20', color: r.color }}
                        data-testid={`role-${r.role_id}`}
                      >
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                        {r.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Member since */}
              <div className="mb-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">{t('app.memberSince')}</p>
                <div className="flex items-center gap-1.5 text-sm text-zinc-300">
                  <Calendar className="h-3.5 w-3.5 text-zinc-500" />
                  {profile.created_at ? new Date(profile.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'Unknown'}
                </div>
                {profile.server_joined_at && (
                  <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-1">
                    <Shield className="h-3 w-3" />
                    Server: {new Date(profile.server_joined_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </div>
                )}
              </div>

              {/* Connections */}
              {profile.connections && Object.keys(profile.connections).length > 0 && (
                <div className="mb-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">{t('app.connections')}</p>
                  <div className="space-y-1">
                    {Object.entries(profile.connections).map(([k, v]) => (
                      <div key={k} className="text-xs text-zinc-400">
                        <span className="text-zinc-500 capitalize">{k}:</span> {v}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {!isSelf && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={handleSendDM} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-xs h-8" data-testid="profile-send-dm">
                    <MessageCircle className="h-3.5 w-3.5 mr-1" />
                    {t('app.sendDM')}
                  </Button>
                  {isFriend ? (
                    <Button size="sm" variant="outline" onClick={handleRemoveFriend} className="border-white/10 text-xs h-8 text-red-400 hover:bg-red-500/10 hover:text-red-400" data-testid="profile-remove-friend">
                      <UserMinus className="h-3.5 w-3.5 mr-1" />
                      {t('app.removeFriend')}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={handleAddFriend} className="border-white/10 text-xs h-8 hover:bg-white/5" data-testid="profile-add-friend">
                      <UserPlus className="h-3.5 w-3.5 mr-1" />
                      {t('app.addFriend')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
